import { brandingToTokens, contrastRatio, hexToRgb, type TenantBranding } from '@auctisync/color';
import type { BidderPreset } from '@auctisync/shared';

/**
 * Pure logic behind Admin → Branding.
 *
 * Kept out of the component so the rules that decide what reaches the database
 * are unit-tested without a DOM — the same split `copilotInputCard.ts` uses for
 * the onboarding card.
 *
 * 🔴 WHY "WHAT CHANGED" IS THE UNIT, NOT "THE WHOLE FORM".
 *
 * `set_branding` reads a null argument as "leave this alone" and an EMPTY STRING
 * as "clear it". Those are different instructions, and a form that always sent
 * all five fields could not express the first — every save would rewrite every
 * key, so two owners saving different tabs would clobber each other, and any key
 * the form does not yet know about would be at risk the moment someone pressed
 * Save. Sending only genuinely-changed fields keeps a save narrow.
 */

/**
 * The hex shapes `set_branding` accepts, mirrored exactly.
 *
 * ⚠️ MUST MATCH THE RPC: `^#?[0-9a-f]{3}([0-9a-f]{3})?$`, case-insensitive —
 * three or six digits, the `#` optional. Diverging here means either a colour
 * the form rejects and the database would have taken, or a doomed round trip
 * ending in a raw Postgres error. The server validates authoritatively; this
 * exists to say so in English first.
 */
const HEX = /^#?[0-9a-f]{3}([0-9a-f]{3})?$/i;

export function isValidHex(value: string): boolean {
  return HEX.test(value.trim());
}

/**
 * A typed hex normalised to `#rrggbb`, or null when it is not a colour.
 *
 * Three-digit shorthand is expanded, because `<input type="color">` cannot show
 * `#abc` and would silently render black beside a value the owner typed and the
 * database would happily accept.
 */
export function normaliseHex(value: string): string | null {
  const raw = value.trim();
  if (!HEX.test(raw)) return null;
  const digits = raw.startsWith('#') ? raw.slice(1) : raw;
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((c) => c + c)
          .join('')
      : digits;
  return `#${full.toLowerCase()}`;
}

export interface BrandingForm {
  /** The house's primary colour. Everything else derives from it. */
  primaryColor: string;
  /** The accent, which the CTA is derived from. Blank = none. */
  accentColor: string;
  /** The house's dark colour. Blank = derive it from the primary. */
  darkColor: string;
  /** The logo's public URL. Blank = no logo. */
  logoUrl: string;
  preset: BidderPreset;
}

export const EMPTY_FORM: BrandingForm = {
  primaryColor: '',
  accentColor: '',
  darkColor: '',
  logoUrl: '',
  preset: 'modern',
};

/** Read the stored branding blob into a form. Absent keys become blank. */
export function formFromBranding(
  branding: TenantBranding | null | undefined,
  preset: BidderPreset,
): BrandingForm {
  const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  return {
    primaryColor: str(branding?.primary_color),
    // The legacy alias folds in, exactly as every reader of this blob does.
    accentColor: str(branding?.accent_color) || str(branding?.secondary_color),
    // `dark_color` first, `deep_color` as the legacy fallback — a house that
    // has not re-saved since the 2026-09-17 rename still shows its colour.
    darkColor: str(branding?.dark_color ?? branding?.deep_color),
    logoUrl: str(branding?.logo_url) || str(branding?.logo),
    preset,
  };
}

export interface BrandingErrors {
  primaryColor?: string;
  accentColor?: string;
  darkColor?: string;
}

/**
 * What the owner must fix before Save is allowed.
 *
 * 🔴 THE PRIMARY CANNOT BE CLEARED, only changed. It is the root every derived
 * value hangs off — the page background, the washes, the hero band, the dark
 * panel when no `dark_color` is stored, and the contrast-floored foreground on
 * each. A branding blob with no `primary_color` reads as "no branding at all"
 * (`toTenantBranding` returns null for it), so clearing it would not reset one
 * field, it would silently drop the house back to AuctiSync's own colours
 * across its whole site and app. The other three are genuinely optional and
 * clear to a documented fallback.
 */
export function validateBranding(form: BrandingForm): BrandingErrors {
  const errors: BrandingErrors = {};
  if (form.primaryColor.trim() === '') {
    errors.primaryColor = 'Your main colour is required — the rest of your site is built from it.';
  } else if (!isValidHex(form.primaryColor)) {
    errors.primaryColor = 'Use a hex colour, like #556677.';
  }
  if (form.accentColor.trim() !== '' && !isValidHex(form.accentColor)) {
    errors.accentColor = 'Use a hex colour, like #faf1a5.';
  }
  if (form.darkColor.trim() !== '' && !isValidHex(form.darkColor)) {
    errors.darkColor = 'Use a hex colour, like #56555b.';
  }
  return errors;
}

export function hasErrors(errors: BrandingErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** The arguments `set_branding` takes. Only changed fields appear. */
export interface BrandingArgs {
  p_tenant_id: string;
  p_primary_color?: string;
  p_accent_color?: string;
  /** The RPC accepts `p_deep_color` too, for builds predating the rename — see
   *  the 2026-09-17 migration. New callers send this one. */
  p_dark_color?: string;
  p_logo_url?: string;
  p_preset?: string;
}

/**
 * The narrowest call that turns `saved` into `next`.
 *
 * A field that did not change is OMITTED (the RPC leaves it alone); one that was
 * emptied is sent as `''` (the RPC clears it); one that changed is sent
 * normalised. Colours normalise to `#rrggbb` so the stored value is the one the
 * swatch and every downstream reader agree on.
 */
export function brandingArgs(
  tenantId: string,
  next: BrandingForm,
  saved: BrandingForm,
): BrandingArgs {
  const args: BrandingArgs = { p_tenant_id: tenantId };

  const colour = (
    key: 'p_primary_color' | 'p_accent_color' | 'p_dark_color',
    a: string,
    b: string,
  ): void => {
    const an = a.trim() === '' ? '' : (normaliseHex(a) ?? a.trim());
    const bn = b.trim() === '' ? '' : (normaliseHex(b) ?? b.trim());
    if (an !== bn) args[key] = an;
  };

  colour('p_primary_color', next.primaryColor, saved.primaryColor);
  colour('p_accent_color', next.accentColor, saved.accentColor);
  colour('p_dark_color', next.darkColor, saved.darkColor);

  if (next.logoUrl.trim() !== saved.logoUrl.trim()) args.p_logo_url = next.logoUrl.trim();
  if (next.preset !== saved.preset) args.p_preset = next.preset;

  return args;
}

/** True when the form differs from what is stored. */
export function isDirty(next: BrandingForm, saved: BrandingForm): boolean {
  return Object.keys(brandingArgs('x', next, saved)).length > 1;
}

/**
 * What the owner's choices actually produce, resolved through the SAME function
 * the bidder site and the app call.
 *
 * 🔴 DERIVED, NEVER RESTATED. `brandingToTokens` owns the colour maths — the
 * dark panel, the contrast-floored foregrounds, the CTA from the accent. A
 * preview that computed its own version of any of those would be a fourth
 * opinion, and it would be the one the owner trusted while choosing.
 */
export interface BrandingPreview {
  primary: string;
  primaryFg: string;
  deep: string;
  deepFg: string;
  deepMuted: string;
  ctaBg: string;
  ctaFg: string;
  /** True when `dark_color` is stored rather than derived from the primary. */
  deepIsStored: boolean;
  /** Contrast of the logo-bearing band's foreground against the band. */
  bandContrast: number;
}

export function previewFromForm(form: BrandingForm): BrandingPreview | null {
  const primary = normaliseHex(form.primaryColor);
  if (primary === null) return null;

  const branding: TenantBranding = { primary_color: primary };
  const accent = normaliseHex(form.accentColor);
  if (accent !== null) branding.accent_color = accent;
  const dark = normaliseHex(form.darkColor);
  if (dark !== null) branding.dark_color = dark;

  const t = brandingToTokens(branding, 'light');
  const band = t['--brand-primary'] ?? primary;
  const bandFg = t['--brand-primary-fg'] ?? '#ffffff';

  return {
    primary: band,
    primaryFg: bandFg,
    deep: t['--brand-deep'] ?? '#1f1f1f',
    deepFg: t['--text-on-deep'] ?? '#ffffff',
    deepMuted: t['--text-muted-on-deep'] ?? '#cccccc',
    ctaBg: t['--cta-bg'] ?? band,
    ctaFg: t['--cta-fg'] ?? bandFg,
    deepIsStored: dark !== null,
    bandContrast: contrastRatio(hexToRgb(bandFg), hexToRgb(band)),
  };
}
